import { createDispatchOrchestratorClient } from "@tdelivr/api-client/generated/dispatch/dispatch-orchestrator.js";
import { createDriverProfileClient } from "@tdelivr/api-client/generated/driver/driver-profile.js";
import type { Transport } from "@tdelivr/api-client";

/**
 * The two calls the manual-assignment form makes, away from any rendering.
 *
 * The same split as `demand-queue.ts` and for the same reason: what this screen asks the platform
 * can be stated and tested without a browser.
 *
 * Both go to DSP-04 rather than to DSP-01 and DSP-02 directly, and that is the whole shape of this
 * screen. Ranking couriers means reading DRV-05's roster and DRV-06's positions — the two
 * enumerations this platform most deliberately withholds from a public client, DRV-06's contract
 * calling its own the most sensitive one it publishes. Forming an offer means knowing how a
 * candidate reference is built and whose Keycloak subject a courier drives on. A console holds
 * neither, and DSP-04 holds both already for the automatic dispatch.
 */

/** One courier the platform would consider, as DSP-01 ranked them. */
export interface ManualCandidate {
  readonly rank: number;
  readonly driverId: string;
  readonly distanceMeters: number;
  readonly freshness: {
    readonly availabilityAgeSeconds: number;
    readonly locationAgeSeconds: number;
  };
  /**
   * The courier's name, where DRV-01 has one.
   *
   * DSP-01 ranks by `driverId` and returns no name — it is a ranking service and a name is not
   * part of a ranking. The design's own select reads `P. Njoroge · 0.8 km`, so a dispatcher is
   * meant to choose a person rather than an identifier, and DRV-01's roster is where the person is.
   *
   * Optional, because a courier DRV-01 has no profile for is still rankable and still offerable:
   * the identifier is shown instead, which is honest about what is known rather than hiding a
   * courier from the list.
   */
  readonly displayName?: string;
}

export interface ManualAssignClient {
  /** Rank the couriers who could take this demand, now. */
  rank(input: {
    tenantId: string;
    demandId: string;
  }): Promise<{ candidates: readonly ManualCandidate[]; evaluatedAt: string; attempt: number }>;
  /** Send one of them an offer, with the reason the operator gave. */
  sendOffer(input: {
    tenantId: string;
    demandId: string;
    driverId: string;
    reason: string;
  }): Promise<{ offerId: string; driverId: string }>;
}

export function createManualAssignClient(transport: Transport): ManualAssignClient {
  const dispatch = createDispatchOrchestratorClient(transport);
  return {
    async rank(input): Promise<Awaited<ReturnType<ManualAssignClient["rank"]>>> {
      const answer = await dispatch.rankDispatchCandidates(input.tenantId, input.demandId);
      /*
       * The roster, once, rather than a profile read per candidate.
       *
       * The same join the offer monitor and the driver roster already make, and for the same
       * reason: DRV-01 publishes a listing, a ranking is at most five couriers, and five reads to
       * answer one question somebody asked by opening a dialog is five chances to be slow.
       *
       * Failing softly on purpose. A name is how a dispatcher recognises a courier; it is not what
       * identifies them, and a roster that is briefly unreachable should cost the screen a name
       * rather than the ability to send an offer at all.
       */
      const names = await createDriverProfileClient(transport)
        .listDriverProfiles(undefined, "200")
        .then(
          (roster) =>
            new Map(
              roster.driverProfiles.map((profile) => [profile.driverId, profile.displayName]),
            ),
        )
        .catch(() => new Map<string, string>());

      /*
       * Ordered by rank here as well as by DSP-01 there.
       *
       * The design's hint promises "sorted by distance", and rank *is* distance order — but a
       * promise a screen makes should be a promise the screen keeps rather than one it borrows.
       * Sorting an already-sorted list costs nothing and means the hint stays true if the ranking
       * policy ever orders by something else.
       */
      return {
        candidates: [...answer.candidates]
          .sort((left, right) => left.rank - right.rank)
          .map((candidate) => {
            const displayName = names.get(candidate.driverId);
            return { ...candidate, ...(displayName === undefined ? {} : { displayName }) };
          }),
        evaluatedAt: answer.evaluatedAt,
        attempt: answer.attempt,
      };
    },

    async sendOffer(input): Promise<Awaited<ReturnType<ManualAssignClient["sendOffer"]>>> {
      const answer = await dispatch.sendManualRideOffer(input.tenantId, input.demandId, {
        driverId: input.driverId,
        reason: input.reason,
      });
      return answer.offer;
    },
  };
}
